import React from 'react';

import { colors } from '@insurancemenu/imenu-components-lib';
import { CheckCircle, Delete, Person, SupervisorAccount } from '@insurancemenu/imenu-components-lib/dist/icons';
import { checkTotalRateCalculation } from 'global/utils/election/subscriberValidation';
import SubscriberPLCardList from './SubscriberPLCardList';
import {
  SubscriberCardStyled,
  SubscriberHeaderStyled,
  HeaderSectionStyled,
  SubscriberNameWrapperStyled,
  SubscriberNameStyled,
  SubscriberActionBarStyled,
  ActionButtonStyled,
  RemoveSubscriberButtonStyled,
  SubscriberNameLabelStyled,
  SubscriberBatchCheckboxStyled,
} from './styles';
import TotalRate from './TotalRate';
import {
  SUB_BATCH_CHECKBOX_TEST_ID,
  SUB_REMOVE_SUBSCRIBER_BUTTON_TEST_ID,
  SUB_MANAGE_DEPENDENTS_BUTTON_TEST_ID,
  SUB_EDIT_SUBSCRIBER_BUTTON_TEST_ID,
  SUB_NAME_TEST_ID,
  SUB_CHECK_CIRCLE_ICON_TEST_ID,
} from '../constants';
import { SubscriberCardProps } from './types';
import SubscriberClassDropdown from './SubscriberClassDropdown';
import IncompleteProfileErrorTag from '../common/IncompleteProfileErrorTag';

const SubscriberCard: React.FC<SubscriberCardProps> = (props) => {
  const {
    subscriber: { id: subId, common, choice, employeeClass, productLineToCarrier },
    products,
    subscriberStatus,
    callbacksMap,
    productLineRates,
    subscriberClassesMap: { showClass = true, employeeClasses, showFullClassOptions = true },
    requiredSubInfo,
    className,
    isEditable = true,
    isAddedToBatchSelection = false,
  } = props;

  const subscriberName = `${common.firstName} ${common.lastName}`;
  const areAllPlansSelected = choice.every(({ productId }) => productId);
  const totalRate = productLineRates.reduce((total, rateInfo) => total + +rateInfo.rate, 0);
  const totalRateCalculationResult = checkTotalRateCalculation({
    subscriber: props.subscriber,
    electionFlow: 'enrollment',
    requiredSubInfo,
  });

  const isIncompleteProfile = totalRateCalculationResult.errorTypes.includes('INCOMPLETE_PROFILE');
  const isAtLeastOneFooterButtonVisible =
    callbacksMap?.removeSubscriber || callbacksMap?.manageDependents || callbacksMap?.editSubscriber;

  const addToBatchSelection = (): void => {
    callbacksMap.addToBatchSelection?.(subId);
  };

  const onRemoveSubscriber = (): void => {
    callbacksMap.removeSubscriber?.(subId);
  };

  const onManageDependents = (): void => {
    callbacksMap.manageDependents?.(subId);
  };

  const onEditSubscriber = (): void => {
    callbacksMap.editSubscriber?.(subId);
  };

  return (
    <SubscriberCardStyled
      $isAddedToBatchSelection={isAddedToBatchSelection}
      $isEditable={isEditable}
      className={className}
    >
      {isIncompleteProfile && (
        <IncompleteProfileErrorTag isAddedToBatchSelection={isAddedToBatchSelection} isEditable={isEditable} />
      )}
      <SubscriberHeaderStyled $isIncompleteProfile={isIncompleteProfile}>
        <HeaderSectionStyled>
          {/* Name with checkbox and selected icon */}
          <SubscriberNameWrapperStyled>
            <SubscriberNameStyled>
              {callbacksMap?.addToBatchSelection && isEditable ? (
                <SubscriberBatchCheckboxStyled
                  name={`$subscriber-${subId}`}
                  id={`$subscriber-${subId}`}
                  label={
                    <SubscriberNameLabelStyled data-test-id={SUB_NAME_TEST_ID}>
                      {subscriberName}
                    </SubscriberNameLabelStyled>
                  }
                  checked={isAddedToBatchSelection}
                  iconSize="2.4rem"
                  highlightColor={colors.blue[600]}
                  onChange={addToBatchSelection}
                  data-test-id={SUB_BATCH_CHECKBOX_TEST_ID}
                />
              ) : (
                <SubscriberNameLabelStyled data-test-id={SUB_NAME_TEST_ID}>{subscriberName}</SubscriberNameLabelStyled>
              )}
              {/* Show the checkCircle icon only when the batch checkbox is visible and the subscriber card is editable */}
              {areAllPlansSelected && callbacksMap?.addToBatchSelection && isEditable && (
                <CheckCircle width="2.4rem" color={colors.blue[600]} data-test-id={SUB_CHECK_CIRCLE_ICON_TEST_ID} />
              )}
            </SubscriberNameStyled>
          </SubscriberNameWrapperStyled>

          {/* Status with additional info */}
          {subscriberStatus}
        </HeaderSectionStyled>

        <HeaderSectionStyled>
          {/* Class Dropdown */}
          {showClass ? (
            <SubscriberClassDropdown
              employeeClasses={employeeClasses}
              showFullClassOptions={showFullClassOptions}
              employeeClass={employeeClass}
              updateClass={isEditable ? callbacksMap?.updateClass : undefined}
            />
          ) : null}

          {/* Total rate block with error message or total rate */}
          <TotalRate totalRate={totalRate} totalRateCalculationResult={totalRateCalculationResult} />
        </HeaderSectionStyled>
      </SubscriberHeaderStyled>

      {/* Body */}
      <SubscriberPLCardList
        subscriber={props.subscriber}
        products={products}
        productLineRates={productLineRates}
        productLineToCarrier={productLineToCarrier}
        requiredSubInfo={requiredSubInfo}
        isEditable={isEditable}
        callbacksMap={callbacksMap}
      />

      {/* Footer/ Action Bar */}
      {isEditable && isAtLeastOneFooterButtonVisible && (
        <SubscriberActionBarStyled>
          {callbacksMap?.removeSubscriber && (
            <RemoveSubscriberButtonStyled
              type="text"
              uppercase
              color="custom"
              customColor={colors.red[600]}
              leftIconProp={<Delete />}
              onClick={onRemoveSubscriber}
              data-test-id={SUB_REMOVE_SUBSCRIBER_BUTTON_TEST_ID}
            >
              Remove Subscriber
            </RemoveSubscriberButtonStyled>
          )}
          {callbacksMap?.manageDependents && (
            <ActionButtonStyled
              type="text"
              uppercase
              color="primary"
              leftIconProp={<SupervisorAccount />}
              onClick={onManageDependents}
              data-test-id={SUB_MANAGE_DEPENDENTS_BUTTON_TEST_ID}
            >
              Manage Dependents
            </ActionButtonStyled>
          )}
          {callbacksMap?.editSubscriber && (
            <ActionButtonStyled
              type="text"
              uppercase
              color="primary"
              leftIconProp={<Person />}
              onClick={onEditSubscriber}
              data-test-id={SUB_EDIT_SUBSCRIBER_BUTTON_TEST_ID}
            >
              Update Employee Profile
            </ActionButtonStyled>
          )}
        </SubscriberActionBarStyled>
      )}
    </SubscriberCardStyled>
  );
};

export default SubscriberCard;
